import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { TablesSlice } from '@hefe/store';
import { Button, Space, Transfer } from 'antd';
import { TransferListProps } from 'antd/es/transfer';
import { TransferDirection } from 'antd/lib/transfer';
import { arrayMoveImmutable } from 'array-move';
import { t } from '@hefe/i18n';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import { Key } from 'antd/es/table/interface';

interface CustomTableColumnTransferComponentProps {
  name: string;
  dataSource: any[];
  columns: any[];
  toolbarItems?: any[];
  loading?: boolean;
  targetKeys: string[];
  setTargetKeys: (keys: string[]) => void;
}

export default function CustomTableColumnTransferComponent(
  props: CustomTableColumnTransferComponentProps
) {
  const { columns, name, targetKeys, setTargetKeys } = props;
  const dispatch = useAppDispatch();

  // Table Store
  const tableStore = useSelector(TablesSlice.tablesSelector)[name] || {
    currentTableKeys: [],
    presets: [],
  };
  // Transfer Modal
  const [selectedSourceKeys, setSelectedSourceKeys] = useState<Key[]>([]);
  const [selectedTargetKeys, setSelectedTargetKeys] = useState<Key[]>([]);

  // Presets
  const [activePreset, setActivePreset] = useState<string>();

  useEffect(() => {
    searchForActivePreset();
  }, [tableStore.presets]);

  useEffect(() => {
    const presets = tableStore?.presets;
    if (presets) {
      const foundPreset = presets.find((preset) => preset.id === activePreset);
      if (foundPreset) {
        const keys = foundPreset.keys;
        setTargetKeys(keys);
        saveTargetKeys(keys);
      }
    }
  }, [activePreset]);

  const saveTargetKeys = (keys: string[]) => {
    dispatch(
      TablesSlice.tablesActions.setTableKeys({
        tableName: name,
        keys: keys,
      })
    );
  };

  const searchForActivePreset = () => {
    const presets = tableStore?.presets;
    if (presets) {
      const foundPreset = presets.find(
        (preset) => preset.keys.join(',') === targetKeys.join(',')
      );

      if (foundPreset) {
        setActivePreset(foundPreset.id);
        return;
      }
    }

    setActivePreset(undefined);
  };

  const handleSelectChange = (
    sourceSelectedKeys: Key[],
    targetSelectedKeys: Key[]
  ) => {
    setSelectedSourceKeys(sourceSelectedKeys);
    setSelectedTargetKeys(targetSelectedKeys);
  };

  const handleChange = (
    nextTargetKeys: Key[],
    direction: TransferDirection,
    moveKeys: any[]
  ) => {
    if (direction === 'right') {
      setTargetKeys([...targetKeys, ...moveKeys]);
    } else {
      const newTargetKeys = targetKeys.filter((key) => !moveKeys.includes(key));
      setTargetKeys(newTargetKeys);
    }
  };

  const sortTargetKeys = (up: boolean) => {
    const currentIndex = targetKeys.indexOf(selectedTargetKeys[0].toString());
    const currentTargetKeys = [...targetKeys];

    if (
      (up && currentIndex > 0) ||
      (!up && currentIndex < targetKeys.length - 1)
    ) {
      const newIndex = up ? currentIndex - 1 : currentIndex + 1;
      const newTargetKeys = arrayMoveImmutable(
        currentTargetKeys,
        currentIndex,
        newIndex
      );

      setTargetKeys(newTargetKeys);
    }
  };

  const renderFooter = (element: TransferListProps<any>) => {
    if (element.direction === 'right') {
      const disabled = selectedTargetKeys.length !== 1;
      return (
        <div style={{ float: 'right', margin: 5 }}>
          <Space>
            <Button
              disabled={disabled}
              size="small"
              icon={<UpOutlined />}
              onClick={() => sortTargetKeys(true)}
            />
            <Button
              disabled={disabled}
              size="small"
              onClick={() => sortTargetKeys(false)}
              icon={<DownOutlined />}
            />
          </Space>
        </div>
      );
    }

    return null;
  };

  return (
    <Transfer
      style={{ marginTop: 30 }}
      listStyle={{
        width: '100%',
      }}
      dataSource={columns}
      titles={[t('available'), t('selected')]}
      targetKeys={targetKeys}
      selectedKeys={[...selectedSourceKeys, ...selectedTargetKeys]}
      onChange={handleChange}
      onSelectChange={handleSelectChange}
      render={(item) => item.title || ''}
      footer={(props) => renderFooter(props)}
    />
  );
}
